import React from 'react';
import { Box, Button, Typography, Alert } from '@mui/material';
import AuthDarkTextField from './AuthDarkTextField';
import AuthMobileCard from './AuthMobileCard';
import {
  darkPrimaryButtonSx,
  darkSecondaryButtonSx,
  darkAlertSx,
  LOGIN_ACCENT,
} from './loginDarkTheme';

export default function LoginOtpStep({
  otp,
  maskedEmail,
  infoMessage,
  errors,
  isSubmitting,
  onOtpChange,
  onSubmit,
  onBack,
  onResend,
  variant = 'desktop',
}) {
  const isDarkMobile = variant === 'mobile-dark';

  const desktopFieldSx = {
    mb: 3,
    '& .MuiOutlinedInput-root': {
      borderRadius: '0',
      backgroundColor: '#fafafa',
      fontSize: '16px',
      height: '56px',
      color: '#212121',
      '&:hover fieldset': { borderColor: '#4ce1b6', borderWidth: '1px' },
      '&.Mui-focused fieldset': { borderColor: '#4ce1b6', borderWidth: '1px' },
      '& fieldset': { borderColor: '#e0e0e0', borderWidth: '1px' },
    },
    '& .MuiInputLabel-root': {
      fontSize: '15px',
      color: '#757575',
      '&.Mui-focused': { color: '#4ce1b6', fontWeight: 500 },
    },
    '& .MuiInputBase-input': {
      fontSize: '16px',
      color: '#212121',
      padding: '16.5px 16px',
      letterSpacing: '0.2em',
      textAlign: 'center',
      fontWeight: 600,
    },
    '& .MuiFormHelperText-root': { fontSize: '12px', marginLeft: '4px', marginTop: '6px' },
  };

  const otpBanner = isDarkMobile ? (
    <Box
      sx={{
        mb: 2,
        p: 1.75,
        borderRadius: '14px',
        border: `1px solid rgba(76, 225, 182, 0.35)`,
        background: 'linear-gradient(135deg, rgba(76, 225, 182, 0.14) 0%, rgba(76, 225, 182, 0.05) 100%)',
      }}
    >
      <Typography variant="body2" sx={{ color: '#cbd5e1', fontSize: '0.85rem', lineHeight: 1.45 }}>
        Enter the{' '}
        <Box component="span" sx={{ color: LOGIN_ACCENT, fontWeight: 700 }}>
          6-digit OTP
        </Box>{' '}
        sent to{' '}
        <Box component="span" sx={{ color: '#f8fafc', fontWeight: 700 }}>
          {maskedEmail || 'your registered email'}
        </Box>
      </Typography>
    </Box>
  ) : (
    <Box
      sx={{
        p: 1.6,
        mb: 3,
        borderRadius: '12px',
        border: '1px solid rgba(76, 225, 182, 0.45)',
        background: 'linear-gradient(135deg, rgba(76, 225, 182, 0.12) 0%, rgba(76, 225, 182, 0.06) 100%)',
      }}
    >
      <Typography variant="body2" sx={{ color: '#263238', fontSize: '12.5px', lineHeight: 1.3, fontWeight: 600 }}>
        Enter the <Box component="span" sx={{ fontWeight: 800 }}>6-digit OTP</Box> sent to{' '}
        <Box component="span" sx={{ color: '#0b5ed7', fontWeight: 800 }}>
          {maskedEmail || 'your registered email'}
        </Box>
        .
      </Typography>
    </Box>
  );

  const otpField = isDarkMobile ? (
    <AuthDarkTextField
      type="text"
      label="One-Time Password"
      name="otp"
      value={otp}
      onChange={onOtpChange}
      autoFocus
      inputProps={{ inputMode: 'numeric', maxLength: 6, pattern: '[0-9]*' }}
      error={!!errors.otp}
      helperText={errors.otp ? (Array.isArray(errors.otp) ? errors.otp.join(', ') : errors.otp) : ''}
      sx={{
        '& .MuiInputBase-input': {
          letterSpacing: '0.25em',
          textAlign: 'center',
          fontWeight: 600,
        },
      }}
    />
  ) : (
    <AuthDarkTextField
      type="text"
      label="One-Time Password"
      name="otp"
      value={otp}
      onChange={onOtpChange}
      autoFocus
      inputProps={{ inputMode: 'numeric', maxLength: 6, pattern: '[0-9]*' }}
      error={!!errors.otp}
      helperText={errors.otp ? (Array.isArray(errors.otp) ? errors.otp.join(', ') : errors.otp) : ''}
      sx={desktopFieldSx}
    />
  );

  const content = (
    <>
      {otpBanner}

      {infoMessage && !isDarkMobile && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: '12px' }}>
          {infoMessage}
        </Alert>
      )}

      {errors.non_field_errors && (
        <Alert severity="error" sx={isDarkMobile ? darkAlertSx : { mb: 3, borderRadius: '12px' }}>
          {Array.isArray(errors.non_field_errors)
            ? errors.non_field_errors.map((error, index) => <div key={index}>{error}</div>)
            : errors.non_field_errors}
        </Alert>
      )}

      <Box component="form" onSubmit={onSubmit} noValidate sx={{ width: '100%' }}>
        {otpField}

        {isDarkMobile ? (
          <>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isSubmitting || otp.length !== 6}
              sx={{ ...darkPrimaryButtonSx, mb: 1.5 }}
            >
              {isSubmitting ? 'Verifying...' : 'Verify OTP'}
            </Button>
            <Button
              type="button"
              fullWidth
              variant="outlined"
              onClick={onResend}
              disabled={isSubmitting}
              sx={{
                ...darkSecondaryButtonSx,
                borderColor: 'rgba(76, 225, 182, 0.4)',
                color: LOGIN_ACCENT,
                '&:hover': { borderColor: LOGIN_ACCENT, backgroundColor: 'rgba(76, 225, 182, 0.1)' },
              }}
            >
              Resend OTP
            </Button>
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
              <Button
                type="button"
                variant="outlined"
                onClick={onBack}
                disabled={isSubmitting}
                sx={{ py: 1.5, borderRadius: '0', textTransform: 'none', fontWeight: 600, flex: 1 }}
              >
                Back
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting || otp.length !== 6}
                sx={{
                  py: 1.5,
                  backgroundColor: '#000000',
                  color: '#ffffff',
                  borderRadius: '0',
                  textTransform: 'none',
                  fontWeight: 600,
                  flex: 1.1,
                  '&:disabled': { backgroundColor: '#bdbdbd' },
                }}
              >
                {isSubmitting ? 'Verifying...' : 'Verify OTP'}
              </Button>
            </Box>
            <Button
              type="button"
              fullWidth
              variant="contained"
              onClick={onResend}
              disabled={isSubmitting}
              sx={{
                py: 1.5,
                borderRadius: '0',
                textTransform: 'none',
                fontWeight: 700,
                backgroundColor: '#000000',
                color: '#ffffff',
                '&:disabled': { backgroundColor: '#9e9e9e' },
              }}
            >
              Resend OTP
            </Button>
          </>
        )}
      </Box>
    </>
  );

  if (isDarkMobile) {
    return (
      <AuthMobileCard
        title="Verify OTP"
        subtitle="Check your email for the verification code."
        onBack={onBack}
        backDisabled={isSubmitting}
      >
        {content}
      </AuthMobileCard>
    );
  }

  return content;
}
